import styled from 'styled-components';
import { CheckmarkIc } from '@dsch/dd-icons';
import { media } from '../../helpers/breakpoints';

import { CarSummaryPanel } from '../../components/CarValuationPage/components/CarSummaryPanel/CarSummaryPanel';
import { InfoPanelList } from '../../components/CarValuationPage/components/InfoPanelList/InfoPanelList';
import { KeyInfoShowMore } from '../../components/CarValuationPage/components/KeyInfoShowMore/KeyInfoShowMore';
import { AllianzInsuranceRenewal } from '../../components/CarValuationPage/components/AllianzInsuranceRenewal/AllianzInsuranceRenewal';

import { CarValuationResponse } from '../../api/types/CarValuationApiTypes';
import {
  Container,
  SectionBreakHeader,
} from '../../components/HubPages/components/SectionBreakHeader/SectionBreakHeader';
import { DetailList } from '../../components/Toolkit/DetailList/DetailList';
import { InfoTitle } from '../../components/Toolkit/InfoTitle/InfoTitle';
import { ButtonLink } from '../../components/Toolkit/Button/ButtonLink';
import { Notification } from '../../components/Toolkit/Notification/Notification';
import { IMetaInfoItem } from '../../components/Toolkit/PublishFilters/types/index';
import { TCarValuationPageNames, PAGE } from '../../helpers/pages';
import { carValuationPriceFormatter } from '../../helpers/formatting';
import { type InsuranceRenewalDetails } from '../../pages/car-valuation/[registration]/[mileage]';
import { useCarValuation } from '../../components/CarValuationPage/hooks/useCarValuation';
import { useMyVehicleUpdate } from '../../components/CarValuationPage/hooks/useMyVehicleUpdate';
import type { StrictOption } from '../../components/Toolkit/Inputs/Select';
import { Select } from '../../components/Toolkit/Inputs/Select';
import { useAllianzInsuranceRenewal } from '../../components/CarValuationPage/components/AllianzInsuranceRenewal/useAllianzInsuranceRenewal';
import { ValuationSection } from '../../components/CarValuationPage/components/Panel';

interface ICarValuation {
  cdnUrl: string;
  carValuationResponse: CarValuationResponse;
  keyInfoDisplayAttributes?: IMetaInfoItem[];
  vehicleMetaTitle: string;
  trim?: string;
  pageName: TCarValuationPageNames;
  vehicleInfo: {
    make: string;
    model: string;
    year: string;
    bodyType: string;
    fuelType: string;
    transmission: string;
  };
  registration: string;
  mileage: string;
  insuranceRenewalDetails?: InsuranceRenewalDetails;
}

export const PageWrapper = styled.div`
  margin: 0 auto;
`;

const ContentWrapper = styled.div`
  margin: ${({ theme }) =>
    `0 ${theme.spacing.S8} ${theme.spacing.L40} ${theme.spacing.S8}`};

  ${media.large} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const SectionBreakHeaderWrapper = styled.div`
  width: 100%;

  ${media.large} {
    width: 760px;
  }

  ${Container} {
    margin: ${({ theme }) => `${theme.spacing.M24} 0 ${theme.spacing.M16} 0`};

    ${media.large} {
      margin: ${({ theme }) => `${theme.spacing.L40} 0 ${theme.spacing.M24} 0`};
    }
  }
`;

const SectionBreak = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const SDetailList = styled(DetailList)`
  row-gap: ${({ theme }) => theme.spacing.S8};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const SellingPanels = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${({ theme }) => theme.spacing.M16};
  align-items: flex-start;

  width: 100%;

  ${media.large} {
    grid-template-columns: 1fr 1fr;
    max-width: 760px;
  }
`;

const PrivateSalePanel = styled.div<{ isInstantOfferPage?: boolean }>`
  background: ${({ theme }) => theme.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => theme.spacing.M16};
  order: ${({ isInstantOfferPage }) => (isInstantOfferPage ? '2' : '')};

  align-self: stretch;
  display: flex;
  flex-direction: column;

  ${media.large} {
    padding: ${({ theme }) => theme.spacing.M24};
  }
`;

const DealerSalePanel = styled.div`
  background: ${({ theme }) => theme.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => theme.spacing.M16};

  align-self: stretch;
  display: flex;
  flex-direction: column;
  ${media.large} {
    padding: ${({ theme }) => theme.spacing.M24};
  }
`;

const SButtonLink = styled(ButtonLink)`
  margin-top: auto;
`;

const SInfoTitle = styled(InfoTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const SNotification = styled(Notification)`
  color: ${({ theme }) => theme.colors.GREY_DARK};
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M16}`};
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  span {
    color: ${({ theme }) => theme.colors.GREY_DARK};
    ${({ theme }) => theme.fontSize.B12};
  }
`;

const PriceRange = styled.p`
  ${({ theme }) => theme.fontSize.M16};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const assumptions = [
  'No exterior, interior or mechanical damage to your vehicle',
  'Full service history',
  'Two sets of keys',
  'Valid road tax',
  'Valid NCT (if applicable)',
  'Never been declared as an insurance write-off or damaged in an accident',
];

export const HeroImage = styled.div<{
  heroImage: string;
  desktopHeroImage: string;
}>`
  position: absolute;
  top: 0;
  width: 100%;

  background: linear-gradient(0deg, rgba(0, 0, 0, 0.48), rgba(0, 0, 0, 0.48)),
    url('${({ heroImage }) => `${heroImage}`}') no-repeat;
  background-size: cover;
  height: 360px;

  ${media.large} {
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.48), rgba(0, 0, 0, 0.48)),
      url('${({ desktopHeroImage }) => `${desktopHeroImage}`}') no-repeat center;
    background-size: cover;
    height: 400px;
  }
`;

function CarValuationPage({
  cdnUrl,
  carValuationResponse,
  vehicleMetaTitle = '',
  trim = '',
  keyInfoDisplayAttributes,
  registration = '',
  mileage = '',
  pageName,
  vehicleInfo,
  insuranceRenewalDetails,
}: ICarValuation) {
  const { getCurrentInsuranceRenewalMonth } = useCarValuation({
    registration,
    insuranceRenewalDetails,
  });

  const { updateMyVehicle } = useMyVehicleUpdate({
    mileage,
    registration,
    source: 'VALUATION',
  });

  const onSelectInsuranceRenewalMonth = (selectedMonth?: StrictOption) => {
    if (selectedMonth?.label) {
      updateMyVehicle(selectedMonth.label);
    }
  };

  const {
    handleSelectChange,
    currentInsuranceRenewalMonthOption,
    selectOptions,
  } = useAllianzInsuranceRenewal({
    onSelectInsuranceRenewalMonth,
    currentInsuranceRenewalMonth: getCurrentInsuranceRenewalMonth(),
  });

  const {
    expectedDealerOfferRange: { min, max },
  } = carValuationResponse;

  const heroImage = `${cdnUrl}/images/hero/private-valuation-hero-mobile.png`;
  const desktopHeroImage = `${cdnUrl}/images/hero/private-valuation-hero-desktop.png`;

  const heroText =
    pageName === PAGE.INSTANT_OFFER_VALUATION
      ? 'Get the best offer from a trusted dealership and sell your car hassle-free'
      : 'How much is your car worth right now?';

  return (
    <PageWrapper>
      <HeroImage desktopHeroImage={desktopHeroImage} heroImage={heroImage} />

      <CarSummaryPanel
        text={heroText}
        registration={registration}
        mileage={mileage}
        pageName={pageName}
        vehicleMetaTitle={vehicleMetaTitle}
        carValuationResponse={carValuationResponse}
        cdnUrl={cdnUrl}
        vehicleInfo={vehicleInfo}
      />

      {/* Page Content */}
      <ContentWrapper>
        {pageName === PAGE.PRIVATE_VALUATION && (
          <>
            <SectionBreakHeaderWrapper>
              <SectionBreakHeader title="Choose how you want to sell" />
            </SectionBreakHeaderWrapper>

            <SellingPanels>
              <PrivateSalePanel
              // Todo: Uncomment when we bring these panel back to the instant offer page

              // isInstantOfferPage={Boolean(
              //   pageName === PAGE.INSTANT_OFFER_VALUATION,
              // )}
              >
                <SInfoTitle
                  title="Why sell your car on DoneDeal"
                  className="common-margin-bottom"
                  variant="SMALL"
                />
                <SDetailList
                  singleColumnOnDesktop
                  list={[
                    {
                      icon: <CheckmarkIc width="16" height="16" />,
                      title:
                        'Reach hundreds of thousands of genuine buyers all over Ireland',
                    },
                    {
                      icon: <CheckmarkIc width="16" height="16" />,
                      title:
                        'Choose from a host of advertising options to help you stand out from the crowd',
                    },
                    {
                      icon: <CheckmarkIc width="16" height="16" />,
                      title: 'Sell it your way and get the best possible price',
                    },
                  ]}
                />

                <SButtonLink ofType="SECONDARY" href="/publish" target="_blank">
                  Sell my car on DoneDeal
                </SButtonLink>
              </PrivateSalePanel>

              <DealerSalePanel>
                <SInfoTitle
                  title="Why sell your car to a trusted dealership"
                  className="common-margin-bottom"
                  variant="SMALL"
                />

                <SDetailList
                  singleColumnOnDesktop
                  list={[
                    {
                      icon: <CheckmarkIc width="16" height="16" />,
                      title: "It's a hassle-free, professional service",
                    },
                    {
                      icon: <CheckmarkIc width="16" height="16" />,
                      title: "It's the quickest way to sell your car",
                    },
                    {
                      icon: <CheckmarkIc width="16" height="16" />,
                      title: 'Get the best possible dealership offer',
                    },
                  ]}
                />
                <SNotification intent="INFO">
                  <>
                    <span>Dealerships will offer between: </span>
                    <PriceRange>
                      {carValuationPriceFormatter.format(min)} -{' '}
                      {carValuationPriceFormatter.format(max)}
                    </PriceRange>
                  </>
                </SNotification>
                <SButtonLink
                  ofType="SECONDARY"
                  href={`/publish-c2b/${registration}/${mileage}`}
                >
                  Sell my car to a trusted dealership
                </SButtonLink>
              </DealerSalePanel>
            </SellingPanels>
          </>
        )}

        <SectionBreakHeaderWrapper>
          <SectionBreakHeader title="Your Vehicle Details" />
        </SectionBreakHeaderWrapper>

        <ValuationSection>
          <KeyInfoShowMore
            infoList={keyInfoDisplayAttributes}
            keyInfoTitle={vehicleMetaTitle}
            keyInfoListItems={[registration, trim, `${mileage}kms`]}
          />
        </ValuationSection>

        <SectionBreak></SectionBreak>

        <ValuationSection>
          <AllianzInsuranceRenewal
            showPoweredByLogo={false}
            showSelectSubtext={true}
          >
            <Select
              id="renewal_month"
              name="renewal_month"
              label={'Enter your renewal month'}
              options={selectOptions}
              onChange={handleSelectChange}
              willUseSubText={false}
              initialValue={currentInsuranceRenewalMonthOption?.value}
              placeholderAsOption
              placeholder={!currentInsuranceRenewalMonthOption?.value}
            />
          </AllianzInsuranceRenewal>
        </ValuationSection>

        <SectionBreak></SectionBreak>

        <InfoPanelList listItems={assumptions} pageName={pageName} />
      </ContentWrapper>
    </PageWrapper>
  );
}

export { CarValuationPage };
